import { NewsletterSubscribe } from "@component/Widget/NewsletterSubscribe/NewsletterSubscribe";
import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
	faHouseWindow as fasHouseWindow,
	faWallet as fasWallet,
	faCreditCard as fasCreditCard,
	faBabyCarriage as fasBabyCarriage,
	faPersonCane as fasPersonCane,
	faDisplayChartUpCircleDollar as fasDisplayChartUpCircleDollar,
	faMagnifyingGlassDollar as fasMagnifyingGlassDollar,
	faBuildingColumns as fasBuildingColumns,
	faCommentsDollar as fasCommentsDollar,
	faHeartPulse as fasHeartPulse,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import piggyBank from "@img/broken-piggy-bank.png";
import { List } from "bm-ui-kit";
import { kebabCase } from "lodash";
import React from "react";
import type { ReactNode } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { LandingContainer } from "./components/LandingContainer";

interface Props {
	variant?: "retail" | "corporate";
}

interface LinkData {
	href: string;
	icon: IconProp;
	text: string;
}

const RETAIL_LINKS: LinkData[] = [
	{
		href: "/lakashitel/",
		icon: fasHouseWindow,
		text: "Lakáshitel",
	},
	{
		href: "/szemelyi-kolcson/",
		icon: fasWallet,
		text: "Személyi kölcsön",
	},
	{
		href: "/bankszamla/",
		icon: fasCreditCard,
		text: "Bankszámlák",
	},
	{
		href: "/babavaro-hitel/",
		icon: fasBabyCarriage,
		text: "Babaváró hitel",
	},
	{
		href: "/nyugdij-megtakaritas/",
		icon: fasPersonCane,
		text: "Nyugdíj-megtakarítás",
	},
];

const CORPORATE_LINKS: LinkData[] = [
	{
		href: "/vallalati/hitel/hitelhez-segitjuk-vallalkozasodat/",
		icon: fasDisplayChartUpCircleDollar,
		text: "Hitelek",
	},
	{
		href: "/vallalati/hitel/",
		icon: fasMagnifyingGlassDollar,
		text: "Vállalati hitelkereső",
	},
	{
		href: "/vallalati/bankszamla/",
		icon: fasBuildingColumns,
		text: "Bankszámlák",
	},
	{
		href: "/vallalati/vallalati-tanacsadas/",
		icon: fasCommentsDollar,
		text: "Vállalati tanácsadás",
	},
	{
		href: "/vallalati/vallalati-egeszsegbiztositas/",
		icon: fasHeartPulse,
		text: "Vállalati egészségbiztosítás",
	},
];

/**
 * @Routing
 *
 * @title "404 Page Not Found"
 * @description "Az oldal nem található"
 * @template "404.php"
 * @route "/not-found/"
 * @propsList "[{"variant":"corporate"}]"
 * @noIndex "true"
 */
export default class Landing extends React.PureComponent<Props> {
	public render(): ReactNode {
		const { variant = "retail" } = this.props;
		const isRetail = variant === "retail";
		const links: LinkData[] = isRetail ? RETAIL_LINKS : CORPORATE_LINKS;

		return (
			<>
				<LandingContainer bgClassName={`bg-solid-${isRetail ? "purple" : "blue"}`}>
					<Row>
						<Col md={8} className="text-white mb-4 mt-5">
							<h1 className="display-1 display-bold mb-3">Hoppá! Ez az oldal nem hozott eredményt!</h1>
							<div className="display-5 d-md-block d-none mb-3">
								<strong>
									Lehet hogy elavult, hibás, vagy törölt tartalomra kattintottál. Választhatsz az
									alábbi lehetőségek közül:
								</strong>
							</div>
							<List itemStyle="tick" className="mb-4">
								<List.Item>Használd a weboldal keresőjét</List.Item>
								<List.Item>
									<a
										href={
											isRetail
												? "/?link_type=not-found-page"
												: "/vallalati/?link_type=not-found-page"
										}
										className="text-white"
									>
										Navigálj a főoldalra
									</a>
								</List.Item>
								<List.Item>Böngéssz népszerű oldalakon:</List.Item>
							</List>
							{links.map(({ href, icon, text }) => (
								<div className="my-2" key={kebabCase(text)}>
									<a
										href={`${href}?link_type=not-found-page`}
										className="text-white d-flex align-items-end"
									>
										<FontAwesomeIcon
											icon={icon}
											className="text-gold display-4 mb-1 mr-2"
											fixedWidth
										/>
										<strong>{text}</strong>
									</a>
								</div>
							))}
						</Col>
						<Col md={4}>
							<div className="text-uppercase text-white text-center mb-2">
								<strong>Hoppá!</strong>
							</div>
							<Card className="bg-white border-0">
								<Card.Header className="bg-primary text-white text-center display-4 py-3">
									<strong>404</strong>
								</Card.Header>
								<Card.Body className="pb-5">
									<img src={piggyBank} alt="Törött malacpersely" />
								</Card.Body>
							</Card>
						</Col>
					</Row>
				</LandingContainer>
				<LandingContainer>
					<div className="text-md-center py-4">
						<div className="display-4">
							<strong>Nem találod, amit keresel?</strong>
						</div>
						Úgy tűnik, hogy elvesztél a pénzügyi labirintusban.{" "}
						<a href={isRetail ? "/?link_type=not-found-page" : "/vallalati/?link_type=not-found-page"}>
							Térj vissza a főoldalra
						</a>
						, vagy vedd fel velünk a kapcsolatot és segítünk eligazodni!
					</div>
				</LandingContainer>
				<LandingContainer bgClassName={`bg-${variant}-gradient`}>
					<NewsletterSubscribe theme="light" variant={variant} />
				</LandingContainer>
			</>
		);
	}
}
